import { nodeTypesAPI } from "api/Campaigns/CampaignAPI";
import React, { useEffect } from "react";
import { Node, Edge, NodeChange, applyNodeChanges } from "reactflow";
import { mapBackendEdgeToFlow } from "components/Flow/Edges/edgeHandlers";

export const onNodesChangeHandler = (
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
  changes: NodeChange[]
) => setNodes((nds) => applyNodeChanges(changes, nds));

export const onNodeClickHandler = (
  node: Node,
  onNodeSelect: (node: Node | null) => void
) => onNodeSelect(node);

export const onNodeDragStopHandler = async (
  node: Node,
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
  updateNodeFn: (id: number, payload: any) => Promise<any>
) => {
  setNodes((nds) => nds.map((n) => (n.id === node.id ? { ...n, position: node.position } : n)));
  try {
    await updateNodeFn(Number(node.id), { position: node.position });
  } catch (error) {
    console.error("Failed to update node position:", error);
  }
};

export const onPaneClickHandler = (onNodeSelect: (node: Node | null) => void) =>
  onNodeSelect(null);

export const reloadFlowData = async (
  id: string,
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>,
  getDataFn: (id: string) => Promise<{ nodes: any[]; edges: any[] }>
) => {
  if (!id) return;

  try {
    const data = await getDataFn(id);

    setNodes(data.nodes || []);

    const incomingEdges = data.edges || [];

    // ✅ If edges already look like ReactFlow edges (camelCase), do not remap
    const looksLikeFlowEdges =
      incomingEdges.length === 0 ||
      "sourceHandle" in incomingEdges[0] ||
      "targetHandle" in incomingEdges[0] ||
      "style" in incomingEdges[0];

    if (looksLikeFlowEdges) {
      setEdges(incomingEdges);
      return;
    }

    // ✅ Otherwise, map backend-shaped edges to flow edges
    const mappedEdges: Edge[] = incomingEdges.map((e: any) => {
      const edgeCategory =
        typeof e.edge_category === "object" ? e.edge_category : null;

      const stroke = edgeCategory?.colour_primary ?? "#000000";
      const color = edgeCategory?.colour_text ?? "#ffffff";

      return {
        id: String(e.id),
        source: String(e.source),
        target: String(e.target),
        sourceHandle: e.source_handle ?? null,
        targetHandle: e.target_handle ?? null,
        animated: Boolean(e.animated),
        type: "standardEdge",
        data: {
          edge_category:
            typeof e.edge_category === "number"
              ? e.edge_category
              : edgeCategory?.id ?? null,
          colour_primary: stroke,
          colour_text: color,
          animated: Boolean(e.animated),
        },
        style: { stroke, color },
      };
    });

    setEdges(mappedEdges);
  } catch (err) {
    console.error("Error reloading nodes/edges:", err);
  }
};




export const useDeleteNodeHotkey = (
  selectedNode: Node | null,
  requestDelete: () => void
) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === "Delete") && selectedNode) {
        e.preventDefault();
        requestDelete(); // ONLY opens dialog
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedNode, requestDelete]);
};




/**
 * Generic Add Node Utility
 * 
 * Handles optimistic node creation and backend persistence.
 */

interface HandleAddNodeProps {
  systemId: string | number;                  // the id of the parent system (e.g., campaignId)
  selectedType: number | string | "";         // the id of the selected node type to create (e.g. id for "Event")
  nodeTypes: any[];                           // all available node types
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<any[]>>;
  project: (pos: { x: number; y: number }) => { x: number; y: number };
  createNodeFn: (payload: any) => Promise<any>;
  getFlowDataFn: (id: string ) => Promise<{ nodes: Node[]; edges: any[] }>;
  onNodesReload?: () => Promise<void>;
  parentKey?: string; // defaults to 'campaign', allows reuse in other systems
}

export const handleAddNode = async ({
  systemId,
  selectedType,
  nodeTypes,
  setNodes,
  setEdges,
  project,
  createNodeFn,
  getFlowDataFn,
  onNodesReload,
  parentKey = "campaign", // default key for Campaign system
}: HandleAddNodeProps) => {
  if (!selectedType) return;
  const selectedNodeType = nodeTypes.find((nt) => nt.id === selectedType);
  if (!selectedNodeType) return;

  const flowWrapper = document.querySelector(".react-flow") as HTMLDivElement;
  const bounds = flowWrapper?.getBoundingClientRect();

  const center = project({
    x: (bounds?.width || 0) / 2,
    y: (bounds?.height || 0) / 2,
  });

  const tempId = `temp_${Date.now()}`;
  const tempNode: Node = {
    id: tempId,
    type: "default",
    position: center,
    data: { label: selectedNodeType.name },
  };

  // Set the local node state optimistically
  setNodes((nds) => [...nds, tempNode]);

  try {
    // ✅ Build payload dynamically
    const payload = {
      name: selectedNodeType.name,
      description: selectedNodeType.description,
      node_type: selectedType,
      position_x: center.x,
      position_y: center.y,
      [parentKey]: Number(systemId), // dynamic foreign key field
    };

    // ✅ Create node via provided API
    await createNodeFn(payload);

    const jsonResponse = await nodeTypesAPI.getById(13);

    // ✅ Reload all flow data
    const data = await getFlowDataFn(String(systemId));
    setNodes(data.nodes || []);
    setEdges((data.edges || []).map(mapBackendEdgeToFlow));

    if (onNodesReload) await onNodesReload();
  } catch (err) {
    console.error("Error adding node:", err);
    // 🔄 Rollback on failure
    setNodes((nds) => nds.filter((n) => n.id !== tempId));
  }
};
